/*
 * faq-accordion.js — homepage FAQ (sections/faq.liquid)
 *
 * Toggle PATTERN lifted from design_handoff_faq_section/reference/faq.js
 * (single-open by default, data-multi="true" opts into multi-open, roving
 * Arrow/Home/End focus), reimplemented over the section's PRE-RENDERED DOM.
 *
 * CONTRACT (do not break):
 *   - NEVER creates, injects, fetches, clones or rewrites content.
 *     No innerHTML, no insertAdjacentHTML, no createElement, no fetch, no <template>.
 *     Every question and answer is server-rendered by sections/faq.liquid at first byte.
 *   - The ONLY things it mutates are:
 *       panel.hidden            (true/false)
 *       trigger aria-expanded   ("true"/"false")
 *     No class is toggled either — the +/- glyph is driven purely by the
 *     [aria-expanded="true"] CSS attribute selector, so state lives in one place.
 *   - Owner override: NOTHING is opened on load. This file never expands a row at
 *     init; the reference's "first question open" default is deliberately dropped.
 *   - With JS off every panel stays hidden but the text is still in the DOM for
 *     crawlers / AI answer engines and for browser find-in-page.
 *
 * Keyboard map (WAI-ARIA Accordion pattern):
 *   Enter / Space  -> native <button> activation (no JS keydown handling needed)
 *   ArrowDown      -> next trigger (wraps to first)
 *   ArrowUp        -> previous trigger (wraps to last)
 *   Home           -> first trigger
 *   End            -> last trigger
 *   Tab            -> unchanged; focus is never trapped.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-iedm-faq]';
  var TRIGGER_SELECTOR = '[data-iedm-faq-trigger]';

  function triggersIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll(TRIGGER_SELECTOR));
  }

  function panelFor(trigger) {
    var id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setOpen(trigger, open) {
    var panel = panelFor(trigger);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open; // the ONLY visibility mechanism
  }

  function onClick(event) {
    var trigger = event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;
    var root = trigger.closest(ROOT_SELECTOR);
    if (!root) return;

    var isOpen = trigger.getAttribute('aria-expanded') === 'true';
    var multi = root.getAttribute('data-multi') === 'true';

    // Single-open accordion (design spec): opening one closes the rest.
    // Clicking the open row still collapses it, so the section can be fully closed.
    if (!multi) {
      triggersIn(root).forEach(function (t) {
        if (t !== trigger) setOpen(t, false);
      });
    }
    setOpen(trigger, !isOpen);
  }

  function onKeydown(event) {
    var key = event.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;

    var trigger = event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;
    var root = trigger.closest(ROOT_SELECTOR);
    if (!root) return;

    var list = triggersIn(root);
    var i = list.indexOf(trigger);
    if (i === -1 || list.length === 0) return;

    var next;
    if (key === 'ArrowDown') next = list[(i + 1) % list.length];
    else if (key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
    else if (key === 'Home') next = list[0];
    else next = list[list.length - 1];

    if (next) {
      event.preventDefault(); // stop page scroll only for the keys we own
      next.focus();
    }
  }

  // Delegated at document level: one listener pair total, works for any number of
  // section instances and survives theme-editor section re-renders with no re-init.
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
})();
